import React from 'react'

import type { ComponentConfig } from '@puckeditor/core'
import { Heading as _Heading } from './HeadingComponent'
import type { HeadingProps as _HeadingProps } from './HeadingComponent'
import { Section } from '../../components/Section'
import { WithLayout, withLayout } from '../../components/Layout'
import { ThemeCustomFields } from '../../theme-config'
import {
  FontWeight,
  ShadowProps,
  TypographyProps,
  fontWeightOptions,
  shadowStyleField,
  applyShadowStyle,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  applyTypography,
} from '../../fields/StyleFields'

export type HeadingProps = WithLayout<{
  align: 'left' | 'center' | 'right'
  text?: string
  color?: string
  level?: _HeadingProps['rank']
  size?: string
  fontWeight?: FontWeight
  letterSpacing?: string
  lineHeight?: string
  shadow?: ShadowProps
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
}>

const levelOptions = [
  { label: '', value: '' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
]

const HeadingInternal: ComponentConfig<{
  props: HeadingProps
  fields: ThemeCustomFields
}> = {
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description: 'Plain-text title; level sets the h1-h6 tag.',
    fields: {
      text: 'Plain text, no HTML.',
      level: 'Level 1 once per page or step; 2 or 3 for section titles.',
      size: 'CSS length, e.g. 32px or 2rem.',
    },
  },
  fields: {
    text: {
      label: 'Content',
      type: 'textarea',
      contentEditable: true,
    },
    color: {
      label: 'Font Color',
      type: 'colorPicker',
    },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    textTransform: textTransformField,
    size: {
      label: 'Font Size',
      type: 'text',
      placeholder: '16px',
    },
    fontWeight: {
      label: 'Font Weight',
      type: 'select',
      options: fontWeightOptions,
    },
    letterSpacing: {
      label: 'Letter Spacing',
      type: 'text',
      placeholder: '1px / 0.1em / 10%',
    },
    lineHeight: {
      label: 'Line Height',
      type: 'text',
      placeholder: '1.5 / 24px / 150%',
    },
    shadow: shadowStyleField,
    level: {
      label: 'Level',
      type: 'select',
      options: levelOptions,
    },
    align: {
      label: 'Alignment',
      type: 'radio',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
  },
  defaultProps: {
    align: 'left',
    text: 'Heading',
    color: '#000',
    level: '1',
    layout: {
      padding: '8px',
    },
  },
  render: ({ align, text, size, level, color, fontWeight, letterSpacing, lineHeight, shadow, fontFamily, fontFamilyFallback, textTransform }) => {
    return (
      <Section>
        <_Heading size={size} rank={level as any} color={color}>
          <span
            style={{
              display: 'block',
              textAlign: align,
              width: '100%',
              fontWeight: fontWeight ? Number(fontWeight) || undefined : undefined,
              letterSpacing: letterSpacing || undefined,
              lineHeight: lineHeight || undefined,
              ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
              ...applyShadowStyle(shadow),
            }}
          >
            {text}
          </span>
        </_Heading>
      </Section>
    )
  },
}

export const Heading = withLayout(HeadingInternal)
